import { PaymentFrameParameters, PaymentFrameStyle } from '../../../../components/src/lib/MonerisIframe/iframe.types';

declare function createPaymentFrame(
  type: 'cvv',
  containerSelector: string,
  parameters: PaymentFrameParameters,
  style: PaymentFrameStyle
): void;
import React, { useCallback, useEffect, useState } from 'react';
import { useIframeEventHandler } from '../../../../components/src/lib/MonerisIframe/iframe.utils';
import {
  DS_FIELD_EVENTS,
  DS_MESSAGE_TYPES,
  DS_SOURCE,
  DS_SYSTEM_EVENTS,
  iframeInputStylesInitial,
  iframeInputStylesInvalid
} from '@trip-ux/constants';

// LOCAL DEV ONLY — read the JWE from `tripx-ux/.env` (gitignored).
// Webpack DefinePlugin (tools/webpack.fe.js + tools/webpack.stateless.fe.js) inlines
// `process.env.DS_IFRAME_TOKEN` at build time for non-prod bundles only — production
// bundles use the Vaulting.io PCI proxy injection path (the `!isLocalhost` branch below)
// so this constant is empty/unused in prod.
//
// To refresh the token:
//   1. Open https://www-uat.manulife-travel.ca/__datastealth/test/parent_payment_development_demo.html
//   2. View source > find <meta name="token" content="..."> under the
//      `// manulifeaffinity-development-3004` comment (matches `npm run start:coverme` port 3004)
//   3. Set in tripx-ux/.env:
//        DS_IFRAME_TOKEN='<paste-jwe-here>'
//   4. Restart the client watcher (`npm run watch:coverme:client`) so DefinePlugin re-reads .env.
const LOCAL_DEV_DS_IFRAME_TOKEN = process.env.DS_IFRAME_TOKEN || '';

export const CvvIFrame = ({ id, onMessage, path, iframeUrl }) => {
  const isLocalhost = globalThis.location.hostname === 'localhost' || globalThis.location.hostname === '127.0.0.1';

  const [handleIframeEvent] = useIframeEventHandler(
    iframeUrl,
    iframeInputStylesInitial,
    {},
    iframeInputStylesInvalid,
    {}
  );
  const fieldIdx = id - 1;
  const handleValidFieldEvent = useCallback(
    (cvv, index) => {
      onMessage({ value: cvv, hasError: false }, index + 1);
    },
    [onMessage]
  );

  const handleInvalidFieldEvent = useCallback(
    index => {
      onMessage({ value: null, hasError: true }, index + 1);
    },
    [onMessage]
  );

  const handleBlurFieldEvent = useCallback(() => {}, [onMessage]);

  const handleFocusFieldEvent = useCallback(() => {}, [onMessage]);

  const handleFieldEvent = useCallback(
    message => {
      const { event, detail: cvv, index } = message;
      switch (event) {
        case DS_FIELD_EVENTS.ON_VALID:
          handleValidFieldEvent(cvv, index);
          break;
        case DS_FIELD_EVENTS.ON_INVALID:
          handleInvalidFieldEvent(index);
          break;
        case DS_FIELD_EVENTS.ON_BLUR:
          handleBlurFieldEvent();
          break;
        case DS_FIELD_EVENTS.ON_FOCUS:
          handleFocusFieldEvent();
          break;
        default:
          break;
      }
    },
    [handleValidFieldEvent, handleInvalidFieldEvent, handleBlurFieldEvent, handleFocusFieldEvent]
  );

  const handleSystemEvent = useCallback(
    message => {
      const { index } = message;
      if (message.event === DS_SYSTEM_EVENTS.ON_ERROR) {
        onMessage({ value: null, hasError: true }, index + 1);
      }
    },
    [onMessage]
  );

  useEffect(() => {
    const handleMessage = e => {
      let message;
      try {
        message = JSON.parse(e.data);
      } catch (error) {
        return;
      }

      const { source, type, target } = message;

      if (source !== DS_SOURCE.FRAME) {
        return;
      }
      if (target === 'cvv') {
        if (type === DS_MESSAGE_TYPES.FIELD) {
          handleFieldEvent(message);
        }

        if (type === DS_MESSAGE_TYPES.SYSTEM) {
          handleSystemEvent(message);
        }
        if (type === DS_MESSAGE_TYPES.IFRAME) {
          const iframeDoc = document.getElementById(`field-protect-cvv-${fieldIdx}`);
          handleIframeEvent({ ...message, iframe: iframeDoc });
        }
      }
    };

    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage, false);
  }, [handleFieldEvent, handleSystemEvent, handleIframeEvent]);

  const containerId = `cvv-${fieldIdx}-container`;
  useEffect(() => {
    if (isLocalhost) {
      return;
    } // Guard clause to prevent execution on localhost

    const cvvParameters = {
      settingsid: `${path}`,
      fieldindex: fieldIdx,
      arialabel: `cvv-${fieldIdx}`,
      title: `cvv-${fieldIdx}`
    };
    const cvvStyle = {
      iframeBorder: '1px solid #c2c3c9',
      iframeScrolling: 'no',
      iframeWidth: '100%',
      iframeHeight: '48px',
      iframeDisplay: 'block'
    };

    createPaymentFrame('cvv', `#${containerId}`, cvvParameters, cvvStyle);
  }, [isLocalhost, path, fieldIdx, containerId]);

  // Localhost: dynamically create iframe in container div, just like prod
  useEffect(() => {
    if (!isLocalhost) {
      return;
    } // Guard clause to prevent execution when not on localhost

    const container = document.getElementById(containerId);
    if (container && !container.querySelector('iframe')) {
      // Check if iframe already exists
      const iframe = document.createElement('iframe');
      iframe.id = `field-protect-cvv-${fieldIdx}`;
      iframe.src = `${iframeUrl}/payment/protect/payment_frame_field.html?e=${LOCAL_DEV_DS_IFRAME_TOKEN}&fieldtype=cvv&fieldindex=${fieldIdx}`;
      iframe.style.border = '0';
      iframe.title = `cvv-${fieldIdx}`;
      iframe.width = '100%';
      iframe.height = '48px';
      iframe.setAttribute('aria-label', `cvv-${fieldIdx}`);
      iframe.setAttribute('scrolling', 'no');
      container.appendChild(iframe);
    }
  }, [isLocalhost, containerId, fieldIdx, iframeUrl]);

  return <div id={containerId}></div>;
};
export default CvvIFrame;
